import React, { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import { api, avatarSrc, formatCurrency, formatApiErrorDetail, toImageSrc } from "@/lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Handshake, ArrowRight, Check, X as XIcon, ChatCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

const STATUS_META = {
  pending: { label: "Pending", color: "bg-amber-500/15 text-amber-400 border-amber-500/30" },
  counter: { label: "Counter-offer", color: "bg-blue-500/15 text-blue-400 border-blue-500/30" },
  accepted: { label: "Accepted", color: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" },
  declined: { label: "Declined", color: "bg-zinc-500/15 text-zinc-400 border-zinc-500/30" },
  in_progress: { label: "In Progress", color: "bg-cyan-500/15 text-cyan-400 border-cyan-500/30" },
  closed_won: { label: "Closed Won", color: "bg-emerald-600/20 text-emerald-300 border-emerald-600/40" },
  closed_lost: { label: "Closed Lost", color: "bg-red-500/15 text-red-400 border-red-500/30" },
};

export default function ReferralsPage() {
  const { user } = useAuth();
  const [refs, setRefs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/referrals");
      setRefs(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (!user || typeof user !== "object") return null;
  const myId = user.id;
  const sent = refs.filter((r) => r.from_agent_id === myId);
  const received = refs.filter((r) => r.to_agent_id === myId);

  return (
    <div className="min-h-screen p-6 md:p-10 pb-24" data-testid="referrals-page">
      <div className="max-w-5xl mx-auto">
        <div className="mb-8">
          <div className="text-[10px] uppercase tracking-[0.3em] text-gold mb-2">Co-broking</div>
          <h1 className="font-display text-4xl md:text-5xl tracking-tight">Referrals</h1>
          <p className="text-sm text-zinc-500 mt-2">Refer your clients to a friend agent&apos;s inventory and split the commission.</p>
        </div>

        <Tabs defaultValue="received">
          <TabsList className="bg-ink-700 border hairline">
            <TabsTrigger value="received" data-testid="referrals-tab-received">
              Received {received.length > 0 && <span className="ml-1.5 text-gold">({received.length})</span>}
            </TabsTrigger>
            <TabsTrigger value="sent" data-testid="referrals-tab-sent">
              Sent {sent.length > 0 && <span className="ml-1.5 text-gold">({sent.length})</span>}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="received" className="mt-5 space-y-3">
            {loading ? <ListSkeleton /> : received.length === 0
              ? <Empty text="No referrals sent your way yet." />
              : received.map((r) => <ReferralCard key={r.id} ref_={r} myId={myId} onOpen={() => setActive(r)} />)}
          </TabsContent>
          <TabsContent value="sent" className="mt-5 space-y-3">
            {loading ? <ListSkeleton /> : sent.length === 0
              ? <Empty text={`Visit any friend agent's property and click "Refer my client" to start.`} />
              : sent.map((r) => <ReferralCard key={r.id} ref_={r} myId={myId} onOpen={() => setActive(r)} />)}
          </TabsContent>
        </Tabs>
      </div>

      <ReferralDialog ref_={active} myId={myId} onClose={() => setActive(null)} onChanged={load} />
    </div>
  );
}

function ListSkeleton() {
  return (
    <>
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className="card-surface rounded-xl p-5">
          <Skeleton className="h-4 w-1/3 bg-white/[0.08]" />
          <Skeleton className="h-3 w-1/2 mt-3 bg-white/[0.06]" />
          <Skeleton className="h-3 w-2/3 mt-2 bg-white/[0.05]" />
        </div>
      ))}
    </>
  );
}

function Empty({ text }) {
  return (
    <div className="card-surface rounded-xl p-10 text-center text-zinc-500" data-testid="referrals-empty">
      <Handshake size={28} className="mx-auto text-gold mb-3" weight="duotone" />
      {text}
    </div>
  );
}

function ReferralCard({ ref_, myId, onOpen }) {
  const meta = STATUS_META[ref_.status] || STATUS_META.pending;
  const isSent = ref_.from_agent_id === myId;
  const counterparty = isSent ? ref_.referee : ref_.referrer;
  const finalSplit = ref_.counter_split ?? ref_.proposed_split;
  return (
    <button
      type="button"
      onClick={onOpen}
      data-testid={`referral-card-${ref_.id}`}
      className="w-full text-left card-surface rounded-xl p-5 transition-all hover:-translate-y-[1px] hover:border-gold/30"
    >
      <div className="flex items-start gap-4">
        {ref_.property?.image_url ? (
          <img src={toImageSrc(ref_.property.image_url)} alt="" className="h-16 w-16 rounded-md object-cover shrink-0" />
        ) : (
          <div className="h-16 w-16 rounded-md bg-ink-700 shrink-0 flex items-center justify-center"><Handshake size={20} className="text-gold" /></div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <div className="font-display text-lg truncate">{ref_.property?.title || "Property"}</div>
              <div className="text-xs text-zinc-500 truncate">{ref_.property?.location}</div>
            </div>
            <Badge variant="outline" className={`${meta.color} text-xs`} data-testid={`referral-status-${ref_.id}`}>
              {meta.label}
            </Badge>
          </div>
          <div className="grid sm:grid-cols-3 gap-3 mt-3 text-xs">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">{isSent ? "Referred to" : "Referred by"}</div>
              <div className="flex items-center gap-2 mt-1">
                <img src={avatarSrc(counterparty?.avatar_url)} alt="" className="h-5 w-5 rounded-full object-cover" />
                <span className="truncate">{counterparty?.name || "—"}</span>
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Client</div>
              <div className="mt-1 truncate">{ref_.client?.name || "—"}{ref_.client?.budget ? ` · ${formatCurrency(ref_.client.budget)}` : ""}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Split (referrer / referee)</div>
              <div className="mt-1 font-mono">{finalSplit}% / {100 - finalSplit}%</div>
            </div>
          </div>
        </div>
      </div>
    </button>
  );
}

function ReferralDialog({ ref_, myId, onClose, onChanged }) {
  const [counter, setCounter] = useState("");
  const [note, setNote] = useState("");
  const [acting, setActing] = useState(false);
  if (!ref_) return null;
  const isReferee = ref_.to_agent_id === myId;
  const isReferrer = ref_.from_agent_id === myId;
  const finalSplit = ref_.counter_split ?? ref_.proposed_split;
  const canRespond = isReferee && ["pending", "counter"].includes(ref_.status);
  const canProgress = ["accepted", "in_progress"].includes(ref_.status);

  const act = async (action, extras = {}) => {
    setActing(true);
    try {
      await api.patch(`/referrals/${ref_.id}`, { action, note, ...extras });
      toast.success("Updated");
      setNote(""); setCounter("");
      onChanged?.();
      onClose?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    } finally { setActing(false); }
  };
  const addNote = async () => {
    if (!note.trim()) return;
    setActing(true);
    try {
      await api.post(`/referrals/${ref_.id}/notes`, { note });
      setNote("");
      onChanged?.();
      toast.success("Note added");
    } catch (e) {
      toast.error("Failed");
    } finally { setActing(false); }
  };

  return (
    <Dialog open={!!ref_} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl bg-ink-800 border-white/10">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{ref_.property?.title || "Referral"}</DialogTitle>
          <div className="text-xs text-zinc-500">{ref_.property?.location} · {formatCurrency(ref_.property?.price)}</div>
        </DialogHeader>
        <div className="grid sm:grid-cols-2 gap-4 text-sm">
          <Block label="Referrer">
            <Person p={ref_.referrer} /></Block>
          <Block label="Referee">
            <Person p={ref_.referee} /></Block>
          <Block label="Client">
            <div>{ref_.client?.name}</div>
            {ref_.client?.budget > 0 && <div className="text-xs text-gold font-mono">{formatCurrency(ref_.client.budget)}</div>}
            {ref_.client?.requirement && <div className="text-xs text-zinc-500 mt-1">{ref_.client.requirement}</div>}
          </Block>
          <Block label="Commission split (referrer / referee)">
            <div className="font-mono text-lg">{finalSplit}% / {100 - finalSplit}%</div>
            {ref_.counter_split !== null && ref_.counter_split !== undefined && (
              <div className="text-[11px] text-zinc-500">Counter from {finalSplit === ref_.counter_split ? "referee" : "—"}</div>
            )}
          </Block>
        </div>

        {/* Activity timeline */}
        <div className="mt-3">
          <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-2">Activity</div>
          <div className="space-y-2 max-h-48 overflow-y-auto pr-1">
            {(ref_.activity || []).map((ev, i) => (
              <div key={i} className="text-xs border-l-2 border-gold/40 pl-3 py-1">
                <span className="text-zinc-300 capitalize">{ev.type?.replace("_", " ")}</span>
                {ev.split !== undefined && <span className="text-gold ml-2 font-mono">{ev.split}%</span>}
                {ev.note && <div className="text-zinc-500 mt-0.5">{ev.note}</div>}
                <div className="text-[10px] text-zinc-600 mt-0.5">{new Date(ev.at).toLocaleString()}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Note input */}
        <div className="mt-2">
          <Textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Add a note for this thread…"
            rows={2}
            className="bg-ink-700 border-white/10"
            data-testid="referral-note-input"
          />
        </div>

        <DialogFooter className="gap-2 flex-wrap">
          {note.trim() && (
            <Button variant="outline" onClick={addNote} disabled={acting} data-testid="referral-add-note">
              <ChatCircle size={14} className="mr-2" /> Add note
            </Button>
          )}
          {canRespond && (
            <>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  min={0} max={100}
                  placeholder={`Counter % (was ${finalSplit})`}
                  value={counter}
                  onChange={(e) => setCounter(e.target.value)}
                  className="w-40 bg-ink-700 border-white/10 font-mono"
                  data-testid="referral-counter-input"
                />
                <Button
                  variant="outline" disabled={acting || !counter}
                  onClick={() => act("counter", { counter_split: Number(counter) })}
                  data-testid="referral-counter-btn"
                >Counter</Button>
              </div>
              <Button variant="ghost" onClick={() => act("decline")} disabled={acting} data-testid="referral-decline-btn">
                <XIcon size={14} className="mr-2" /> Decline
              </Button>
              <Button onClick={() => act("accept")} disabled={acting} className="bg-gold text-ink-900 hover:bg-gold-light" data-testid="referral-accept-btn">
                <Check size={14} className="mr-2" /> Accept {finalSplit}% / {100 - finalSplit}%
              </Button>
            </>
          )}
          {canProgress && (isReferee || isReferrer) && (
            <>
              {ref_.status === "accepted" && (
                <Button variant="outline" onClick={() => act("mark_in_progress")} disabled={acting} data-testid="referral-progress-btn">Mark in progress</Button>
              )}
              <Button onClick={() => act("mark_won")} disabled={acting} className="bg-emerald-600 hover:bg-emerald-500 text-white" data-testid="referral-won-btn">Closed Won</Button>
              <Button variant="ghost" onClick={() => act("mark_lost")} disabled={acting} data-testid="referral-lost-btn">Closed Lost</Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Block({ label, children }) {
  return (
    <div className="card-surface rounded-xl p-3">
      <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-1">{label}</div>
      <div>{children}</div>
    </div>
  );
}
function Person({ p }) {
  if (!p) return <div className="text-zinc-500">—</div>;
  return (
    <div className="flex items-center gap-2">
      <img src={avatarSrc(p.avatar_url)} alt="" className="h-6 w-6 rounded-full object-cover" />
      <span>{p.name}</span>
      {p.city && <span className="text-xs text-zinc-500">· {p.city}</span>}
    </div>
  );
}

// Standalone dialog for SUBMITTING a new referral. Used from property detail.
export function CreateReferralDialog({ open, onOpenChange, property, onCreated }) {
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState("");
  const [split, setSplit] = useState(50);
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/clients").then(({ data }) => setClients(data)).catch(() => {});
  }, [open]);

  const submit = async () => {
    if (!clientId) return toast.error("Pick a client");
    setLoading(true);
    try {
      await api.post("/referrals", {
        property_id: property.id,
        to_agent_id: property.owner_id,
        client_id: clientId,
        proposed_split: Number(split),
        notes: note,
      });
      toast.success("Referral sent");
      onCreated?.();
      onOpenChange?.(false);
      setClientId(""); setSplit(50); setNote("");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md bg-ink-800 border-white/10">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Refer my client</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="card-surface rounded-md p-3 text-xs">
            <div className="text-zinc-400">{property?.title}</div>
            <div className="font-mono text-gold mt-1">{formatCurrency(property?.price)}</div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">Which client?</label>
            <select
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
              className="mt-1 w-full bg-ink-700 border border-white/10 rounded-md px-3 py-2"
              data-testid="referral-create-client-select"
            >
              <option value="">— Pick a client —</option>
              {clients.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}{c.budget ? ` · ${formatCurrency(c.budget)}` : ""}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">Proposed split — your %</label>
            <Input
              type="number" min={0} max={100} value={split}
              onChange={(e) => setSplit(e.target.value)}
              className="mt-1 bg-ink-700 border-white/10 font-mono"
              data-testid="referral-create-split"
            />
            <div className="text-[11px] text-zinc-500 mt-1">You {split}% · They {100 - Number(split || 0)}%</div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">Note (optional)</label>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} className="mt-1 bg-ink-700 border-white/10" data-testid="referral-create-note" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={loading} className="bg-gold text-ink-900 hover:bg-gold-light gap-2" data-testid="referral-create-submit">
            Send referral <ArrowRight size={14} />
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
